/**
 *  Allows to rename a particular property for a given type object.
 */
type TUtilRenameProperty<T, K extends keyof T, NewName extends string> = Omit<T, K> & {
  [P in NewName]: T[K];
};

export type TUserIdType = string | number;

export type TDDSuccessResponse = 'Success';
export type TDDSuccessObjectResponse = {
  status: 'Success';
};

export enum EDDCatalogueType {
  all = 'all',
  incomplete = 'incomplete',
  pending = 'pending',
  ingested = 'ingested',
  removed = 'removed',
}

export type TDDUserTokenData = {
  access_token: string;
  user_last_name: string;
  country: string;
};

export type TDDActivateRequestData = {
  user_first_name: string;
  user_last_name: string;
  /** The user's country (2 digit ISO code: https://www.nationsonline.org/oneworld/country_code_list.htm) */
  country: string;
  user_email: string;
  user_password: string;
  user_unique_id: string | number;
};

type TDDUpdateFullData = {
  firstname: string;
  lastname: string;
  country: string;
  email: string;
  password: string;
  user_unique_id: string | number;
};

export type TDDUpdateLastNameRequestData = TUtilRenameProperty<
  TDDUpdateFullData,
  'lastname',
  'new_lastname'
>;
export type TDDUpdateFirstNameRequestData = TUtilRenameProperty<
  TDDUpdateFullData,
  'firstname',
  'new_firstname'
>;
export type TDDUpdateCountryRequestData = TUtilRenameProperty<
  TDDUpdateFullData,
  'country',
  'new_country'
>;
export type TDDUpdateEmailRequestData = TUtilRenameProperty<
  TDDUpdateFullData,
  'email',
  'new_email'
>;
export type TDDUpdatePasswordRequestData = TUtilRenameProperty<
  TDDUpdateFullData,
  'password',
  'new_password'
>;

export type TDDGetUserDetailsResponse = {
  Firstname: string;
  Surname: string;
  Country: string;
  Email: string;
};

export type TDDGetMainArtistsResponse = {
  status: string;
  data: string[];
};

export type TDDGetFeatureArtistsResponse = {
  status: string;
  data: string[];
};

export type TDDGetLanguagesResponse = {
  status: string;
  data: [id: string, name: string][];
};

export type TDDGetGenresResponse = {
  status: string;
  data: [id: string, name: string][];
};

export type TDDGetRecordLabelsResponse = {
  status: string;
  data: [name: string][];
};

export type TDDGetStoresResponse = {
  status: string;
  data: [id: string, name: string][];
};

export type TDDGetCountriesResponse = {
  status: string;
  data: [code: string, name: string][];
};

export enum EDDReleaseStatus {
  incomplete = 'incomplete',
  pending = 'pending',
  inReview = 'inreview',
  ingested = 'ingested',
  delivered = 'delivered',
  removed = 'removed',
  declined = 'declined',
}

export enum EDDReleaseWorldwide {
  yes = 'yes',
  no = 'no',
}

export enum EDDReleaseType {
  new = 'new',
  old = 'old',
}

export enum EDDReleaseFormat {
  album = 'Album',
  single = 'Single',
}

type TDDGetReleaseByIDResponseData = {
  /** Format: `YYYY` */
  copy_right_year: string;
  copyright_holder: string;
  /** Format: `YYYY-MM-DD HH:mm:ss` */
  created_date: string;
  format: EDDReleaseFormat;
  is_worldwide: EDDReleaseWorldwide;
  /** Format: `YYYY-MM-DD` */
  original_rel_date: string;
  /** Format: `YYYY-MM-DD` */
  preorder_date: string;
  primary_genre: string;
  record_label: string;
  /** Artwork contains pre-signed URL in AWS. The URL is valid for about 15 minutes by the time it was requested. Expired URL will return "Access Denied". Invoke another API call to request new access to an Artwork. */
  release_artwork: string;
  /** Comma separated country codes */
  release_country: string;
  /** Format: `YYYY-MM-DD` */
  release_date: string;
  release_id: string;
  release_language: string;
  /** Comma separated */
  release_main_artist: string;
  /** The release key that relates to the release tracks in the distro database */
  release_reference: string;
  /** Comma separated stores IDs */
  release_stores: string;
  release_title: string;
  release_type: EDDReleaseType;
  /** Relative path in the distro database */
  resource_reference_artwork: string;
  secondary_genre: string;
  status: EDDReleaseStatus;
  upc_code: string;
  /** Format: `YYYY-MM-DD HH:mm:ss` */
  update_date: string;
};

export type TDDGetReleaseByIDResponse = {
  status: string;
  data: TDDGetReleaseByIDResponseData;
};

export type TDDReleaseDataAsArray = [
  /** 0: */
  release_id: string,
  /** 1: */
  release_type: EDDReleaseType,
  /** 2: */
  release_title: string,
  /** 3: Comma separated */
  release_main_artist: string,
  /** 4: */
  format: EDDReleaseFormat,
  /** 5: Format: `YYYY-MM-DD` */
  release_date: string,
  /** 6: Format: `YYYY-MM-DD` */
  original_rel_date: string,
  /** 7: Format: `YYYY-MM-DD` */
  preorder_date: string,
  /** 8: */
  release_language: string,
  /** 9: */
  primary_genre: string,
  /** 10: */
  secondary_genre: string,
  /** 11: */
  upc_code: string,
  /** 12: */
  record_label: string,
  /** 13: */
  copy_right_year: string,
  /** 14: */
  copyright_holder: string,
  /** 15: Artwork contains pre-signed URL in AWS. The URL is valid for about 15 minutes by the time it was requested. Expired URL will return "Access Denied". Invoke another API call to request new access to an Artwork. */
  release_artwork: string,
  /** 16; Relative path in the distro database */
  resource_reference_artwork: string,
  /** 17: The release key that relates to the release tracks in the distro database */
  release_reference: string,
  /** 18: Comma separated stores IDs */
  release_stores: string,
  /** 19: Comma separated country codes */
  release_country: string,
  /** 20: */
  is_worldwide: EDDReleaseWorldwide,
  /** 21: Format: `YYYY-MM-DD HH:mm:ss` */
  created_date: string,
  /** 22: Format: `YYYY-MM-DD HH:mm:ss` */
  update_date: string,
  /** 23: */
  status: EDDReleaseStatus,
];

export type TDDGetCatalogueByTypeResponse = {
  status: string;
  data: TDDReleaseDataAsArray[];
};

export enum EDDTrackSongAuthority {
  own = 'own',
  cover = 'purchase',
}

export type TDDTrackDataAsArray = [
  /** 0: */
  trackId: string,
  /** 1: */
  trackName: string,
  /** 2: */
  trackVersionTitle: string,
  /** 3: */
  releaseTitle: string,
  /** 4: Comma separated */
  trackMainArtists: string,
  /** 5: Comma separated */
  trackFeatureArtists: string,
  /** 6: */
  songAuthority: EDDTrackSongAuthority,
  /** 7: Comma separated */
  writerComposer: string,
  /** 8: */
  trackISRC: string,
  /** 9: Track contains pre-signed URL in AWS. The URL is valid for about 15 minutes by the time it was requested. Expired URL will return "Access Denied". Invoke another API call to request new access to Track. */
  trackFile: string,
  /** 10: Number of seconds */
  trackDuration: string,
  /** 11: Comma separated */
  producer: string, // TODO:: needs to be verified
  /** 12: */
  trackOrder: string | null,
  /** 13: The ID of the language fetch with `DistroAPI.getLanguages()` */
  trackLanguage: string,
  /** 14: Number of seconds` */
  tikTokStartTime: string,
  /** 15: */
  releaseUPC: string,
];

export type TDDGetTracksByReleaseResponse = {
  status: string;
  data: TDDTrackDataAsArray[];
};

export type TDDGetTracksResponse = {
  status: string;
  data: TDDTrackDataAsArray[];
};

export type TDDComposerTypeCode = string;
export type TDDComposeTypeName = string;
export type TDDComposerType = [code: TDDComposerTypeCode, name: TDDComposeTypeName];

export type TDDGetComposerTypesResponse = {
  status: string;
  data: TDDComposerType[];
};

/* Spotify Users Response Data */
/* -------------------------------------------------------------------------- */
type TDDSpotifyUsersData = {
  href: string;
  items: TDDSpotifyUsersDataItem[];
  limit: number;
  next: string;
  offset: number;
  previous: string;
  total: number;
};

type TDDSpotifyUsersDataItem = {
  external_urls: TDDSpotifyUsersDataItemExternalUrls;
  followers: TDDSpotifyUsersDataItemFollowers;
  genres: string[];
  href: string;
  id: string;
  images: TDDSpotifyUsersDataItemImage[];
  name: string;
  popularity: number;
  type: string;
  uri: string;
};

type TDDSpotifyUsersDataItemExternalUrls = {
  spotify: string;
};

type TDDSpotifyUsersDataItemFollowers = {
  href: string;
  total: number;
};

type TDDSpotifyUsersDataItemImage = {
  height: number;
  url: string;
  width: number;
};

export type TDDGetSpotifyUsersResponse = {
  'Artist ID': string;
  'Artist Name': string;
  artists: TDDSpotifyUsersData;
};

/* Apple Users Response Data */
/* -------------------------------------------------------------------------- */
type TDDAppleUsersDataItem = {
  amgArtistId: number;
  artistId: number;
  artistLinkUrl: string;
  artistName: string;
  artistType: string;
  primaryGenreId: number;
  primaryGenreName: string;
  wrapperType: string;
};

export type TDDGetAppleUsersResponse = {
  'Artist ID': string;
  'Artist Name': string;
  resultCount: number;
  results: TDDAppleUsersDataItem[];
};

export type TDDGetComposersByTypeResponse = {
  status: string;
  type: TDDComposeTypeName;
  /** Names */
  data: string[];
};

/* -------------------------------------------------------------------------- */

export type TDDGetComposerResponse = {
  status: string;
  data: string[];
};

export type TDDAddMainArtistResponse = {
  status: string;
  /** Added artist name */
  data: string;
};

export type TDDAddFeatureArtistResponse = {
  status: string;
  /** Added artist name */
  data: string;
};

export type TDDAddComposerResponse = {
  status: string;
  /** Added composer name */
  data: string;
  /** Added composer type name */
  type: TDDComposeTypeName;
};

export type TDDAddRecordLabelResponse = {
  status: string;
  /** Added record label name */
  data: string;
};

export type TDDStartReleaseResponse = {
  status: string;
  /** Release ID */
  data: string;
};

export type TDDSetReleaseDateRequestDataForNew = {
  /** The date should be provided in format: `D/M/YYYY`. If the release is an 'old' release then set the release date to "" (not null) */
  release_date: string;
  /**The date should be provided in format: `D/M/YYYY`. This is only valid for new releases. If you don't want a pre order date set this to "" (not null) */
  preorder_date: string;
};

export type TDDSetReleaseDateRequestDataForOld = {
  /** The date should be provided in format: `D/M/YYYY`.
   * The date can not be in the future.
   * If the release is a 'new' release then set the original release date to "" (not null) */
  original_release_date: string;
};

export type TDDSetReleaseDateRequestData = TDDSetReleaseDateRequestDataForNew &
  TDDSetReleaseDateRequestDataForOld & {
    release_type: EDDReleaseType;
  };

export type TDDSetReleaseCopyrightResponse = {
  status: string;
  holder: string;
  year: string;
};

export type TDDSetReleaseCountriesResponse = {
  status: string;
  /** Comma separated country codes */
  data: string;
};

export type TDDSetReleaseGenreResponse = {
  status: string;
  /** Genre name */
  data: string;
};

export type TDDSetReleaseLanguageResponse = {
  status: string;
  /** Language name */
  data: string;
};

export type TDDSetReleaseStoresResponse = {
  status: string;
  /** Commas separated store codes */
  data: string;
};

export type TDDSetReleaseRecordLabelResponse = {
  status: string;
  /** Record label name */
  data: string;
};

export type TDDSetReleaseArtworkResponse = {
  /** Artwork URL */
  status: string;
};

export enum EDDReleaseTrackAuthority {
  /** The track is owned by the user */
  own = 'own',
  /** The track is a cover (licensing details in DD Portal) */
  purchase = 'purchase',
}

export type TDDReleaseTrackComposer = {
  code: string;
  name: string;
};

export type TDDReleaseUploadTrackRequestData = {
  name: string;
  versionTitle: string;
  mainArtists: string[] | 'Various Artists';
  featureArtists?: string[];
  authority: EDDReleaseTrackAuthority;
  composers?: TDDReleaseTrackComposer[];
  /** This field is optional and if left blank the track will have it's isrc generated and added to the DistroDirect database. */
  isrc?: string;
  isExplicit?: boolean;
  /** seconds */
  tikTokStartTime?: number;
  /** language code from `getLanguages()` */
  language?: string;
  file: Blob;
};

export type TDDReleaseUploadTrackResponse = {
  status: string;
  /** Track name */
  data: string;
  id: number;
  ISRC: string;
  /** Track URL */
  track: string;
};

export type TDDSubmitReleaseResponse = {
  status: string;
  /** Release id */
  data: string;
};

export type TDDDiscardCatalogueResponse = {
  status: string;
  release_id: string;
};

export type TDDDeleteTrackResponse = {
  status: string;
  track_id: string;
};

export type TDDGetAnalyticsResponse = {
  /** Iframe url */
  URL: string;
};
